import { LockIcon, ShowOutlineIcon } from '@carboncopyco/icons';
import { Pill, TooltipBubble } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { getBrowserWindowSearchPage } from '../../../shared/constants/constants';
import { isURL, replaceUrl } from '../../../util/path-util';

export const BrowserNavbarInput = ({
  url,
  urlEditable,
  isPreviewPillVisible,
  onSubmitUrl,
}: {
  url: string;
  urlEditable?: boolean;
  isPreviewPillVisible: boolean;
  onSubmitUrl?: (url: string) => void;
}) => {
  const [urlValue, setUrlValue] = useState('');
  const [
    isInputFocused,
    { setTrue: setInputFocusedTrue, setFalse: setInputFocusedFalse },
  ] = useBoolean(false);
  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrlValue(event.target.value);
  };

  useEffect(() => {
    setUrlValue(url);
  }, [url]);

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key !== 'Enter') return;
    const submitUrl = isURL(urlValue)
      ? replaceUrl(urlValue)
      : getBrowserWindowSearchPage(urlValue);
    onSubmitUrl?.(submitUrl);
    if (event.target instanceof HTMLInputElement) {
      event.target.blur();
    }
  };

  const previewPill = useMemo(
    () =>
      isPreviewPillVisible && (
        <div className=" mr-2 bg-ds-natural-600 pl-2">
          <Pill
            label="PREVIEW"
            variant="disabled"
            leftIcon={<ShowOutlineIcon size={11} />}
          />
        </div>
      ),
    [isPreviewPillVisible]
  );

  return (
    <div className="flex-grow min-w-0 max-w-full">
      <TooltipBubble
        title={url}
        placement="top-start"
      >
        <div
          className={classNames(
            'flex items-center border rounded-full border-ds-natural px-5 pb-0.5 h-ds-8 relative',
            isInputFocused
              ? 'border-ds-natural-50 text-ds-natural-50'
              : 'border-ds-natual text-ds-natural',
            {
              'border-ds-natural-500': !urlEditable,
            }
          )}
        >
          {urlEditable ? (
            <>
              <input
                spellCheck={false}
                autoFocus
                className="text-ds-400 truncate text-ellipsis bg-transparent outline-none w-full"
                value={urlValue}
                placeholder={url}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                onFocus={setInputFocusedTrue}
                onBlur={setInputFocusedFalse}
                data-testid="browser-navbar-input"
              />
              {previewPill}
            </>
          ) : (
            <div className="flex w-full align-center space-between">
              <div className="mr-2.5">
                <LockIcon size={12} />
              </div>
              <div className="text-ds-400 truncate text-elipsis w-full">
                {url}
              </div>
              {previewPill}
            </div>
          )}
        </div>
      </TooltipBubble>
    </div>
  );
};
